import { Player } from '../types/player';

/**
 * 队列轮换算法类
 * 负责处理玩家队列的轮换逻辑
 */
export class QueueRotation {
  /**
   * 轮换队列 - 将第一个人移到队尾
   * @param currentQueue 当前队列
   * @returns 轮换后的队列
   */
  static rotateQueue(currentQueue: Player[]): Player[] {
    if (currentQueue.length === 0) {
      return [];
    }

    if (currentQueue.length === 1) {
      return [...currentQueue];
    }

    // 将第一个人移到队尾
    const [first, ...rest] = currentQueue;
    return [...rest, first];
  }

  /**
   * 获取当前参赛者（前4个人）
   * @param queue 当前队列或玩家数组
   * @returns 前4个参赛者，如果人数不足则返回所有人
   */
  static getCurrentPlayers(queue: Player[] | { players: Player[] }): Player[] {
    const players = Array.isArray(queue) ? queue : queue.players;
    return players.slice(0, 4);
  }

  /**
   * 获取休息的人（第5个人及以后）
   * @param queue 当前队列或玩家数组
   * @returns 休息的玩家数组
   */
  static getRestingPlayers(queue: Player[] | { players: Player[] }): Player[] {
    const players = Array.isArray(queue) ? queue : queue.players;
    return players.slice(4);
  }

  /**
   * 获取下一个休息的人（第5个人）
   * @param queue 当前队列
   * @returns 休息的玩家，如果没有则返回null
   */
  static getRestingPlayer(queue: Player[]): Player | null {
    return queue.length > 4 ? queue[4] : null;
  }

  /**
   * 检查是否有足够的人进行比赛
   * @param queue 当前队列或玩家数组
   * @returns 是否可以进行比赛
   */
  static canStartMatch(queue: Player[] | { players: Player[] }): boolean {
    const players = Array.isArray(queue) ? queue : queue.players;
    return players.length >= 4;
  }

  /**
   * 获取队列轮换预览
   * @param queue 当前队列
   * @param rounds 预览轮数
   * @returns 每轮的参赛者和休息者信息
   */
  static getRotationPreview(queue: Player[], rounds: number = 5): Array<{
    round: number;
    players: Player[];
    resting: Player[];
  }> {
    const preview = [];
    let currentQueue = [...queue];

    for (let i = 0; i < rounds; i++) {
      preview.push({
        round: i + 1,
        players: this.getCurrentPlayers(currentQueue),
        resting: this.getRestingPlayers(currentQueue),
      });

      // 为下一轮轮换队列
      currentQueue = this.rotateQueue(currentQueue);
    }

    return preview;
  }

  /**
   * 验证队列的有效性
   * @param queue 队列
   * @returns 验证结果
   */
  static validateQueue(queue: Player[]): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // 检查是否为空
    if (queue.length === 0) {
      errors.push('队列不能为空');
    }

    // 检查是否有重复的玩家
    const playerIds = queue.map(p => p.id);
    const uniqueIds = new Set(playerIds);
    if (playerIds.length !== uniqueIds.size) {
      errors.push('队列中存在重复的玩家');
    }

    // 检查玩家编号是否重复
    const playerNumbers = queue.map(p => p.number);
    const uniqueNumbers = new Set(playerNumbers);
    if (playerNumbers.length !== uniqueNumbers.size) {
      errors.push('队列中存在重复的玩家编号');
    }

    // 检查是否所有玩家都是活跃状态
    const inactivePlayers = queue.filter(p => !p.isActive);
    if (inactivePlayers.length > 0) {
      errors.push(`队列中包含 ${inactivePlayers.length} 个非活跃玩家`);
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * 根据玩家编号排序队列
   * @param queue 队列
   * @returns 排序后的队列
   */
  static sortQueueByNumber(queue: Player[]): Player[] {
    return [...queue].sort((a, b) => a.number - b.number);
  }

  /**
   * 在队列中查找玩家位置
   * @param queue 队列
   * @param playerId 玩家ID
   * @returns 玩家在队列中的位置（从0开始），如果不存在返回-1
   */
  static findPlayerPosition(queue: Player[], playerId: string): number {
    return queue.findIndex(p => p.id === playerId);
  }

  /**
   * 计算玩家需要等待多少轮才能休息
   * @param queue 队列
   * @param playerId 玩家ID
   * @returns 等待轮数，如果玩家不在队列中返回-1
   */
  static calculateWaitingRounds(queue: Player[], playerId: string): number {
    const position = this.findPlayerPosition(queue, playerId);
    
    if (position === -1) {
      return -1;
    }

    // 如果已经在休息位置（位置4及以后）
    if (position >= 4) {
      return 0;
    }

    // 计算需要轮换多少次才能到达休息位置
    const queueLength = queue.length;
    if (queueLength <= 4) {
      return -1; // 永远不会休息
    }

    // 需要轮换的次数 = (4 - 当前位置) % 队列长度
    return (4 - position + queueLength) % queueLength;
  }
}